/**
 * Alignment Check Component
 * A beautiful, mindful check-in experience that replaces the Daily Alignment Signal.
 * 
 * Flow:
 * 1. Opening reflective quote with "Start Alignment Check" button
 * 2. Interactive 1-10 slider with dynamic state words and glow effects
 * 3. Desired state selection (2×4 grid)
 * 4. Transition to Private Suite (5-min preview for free, full access for premium)
 */

import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { 
  Sparkles, 
  ArrowRight, 
  Check,
  X,
  Lock,
  Zap,
  Brain,
  Target,
  Battery,
  Sun,
  Smile,
  Heart,
  Compass
} from "lucide-react";
import { Button } from "./ui/button";
import { trackActivity } from "./ReclaimAssistant";
import { toast } from "sonner";
import axios from "axios";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// State mapping for slider values (1=Disconnected to 10=Connected)
const STATE_MAPPING = {
  1: "Disconnected",
  2: "Closed",
  3: "Overwhelmed",
  4: "Unsettled",
  5: "Neutral",
  6: "Settling",
  7: "Open",
  8: "Aligned",
  9: "Flow",
  10: "Connected"
};

// Desired states with descriptions - User specified 2×4 grid
const DESIRED_STATES = [
  { 
    id: "calm", 
    title: "Calm", 
    description: "Ease your nervous system and settle the mind.",
    icon: Sun,
    color: "#4A6741"
  },
  { 
    id: "clarity", 
    title: "Clarity", 
    description: "Create space for clear thinking.",
    icon: Brain,
    color: "#6366F1"
  },
  { 
    id: "confidence", 
    title: "Confidence", 
    description: "Reconnect with your inner strength.",
    icon: Zap,
    color: "#D4A373"
  },
  { 
    id: "energy", 
    title: "Energy", 
    description: "Recharge and revitalize your being.",
    icon: Battery,
    color: "#F59E0B"
  },
  { 
    id: "focus", 
    title: "Focus", 
    description: "Sharpen attention and presence.",
    icon: Target,
    color: "#3B82F6"
  },
  { 
    id: "peace", 
    title: "Peace", 
    description: "Find stillness within the storm.",
    icon: Compass,
    color: "#8B5CF6"
  },
  { 
    id: "joy", 
    title: "Joy", 
    description: "Open to lightness and delight.",
    icon: Smile,
    color: "#EC4899"
  },
  { 
    id: "connection", 
    title: "Connection", 
    description: "Deepen your bond with self and others.",
    icon: Heart,
    color: "#EF4444"
  }
];

// Get color based on slider value - muted grey/blue to vibrant green
const getSliderColor = (value) => {
  if (value <= 3) return "rgb(107, 114, 128)"; // Muted grey
  if (value <= 4) return "rgb(100, 116, 139)"; // Grey-blue
  if (value <= 5) return "rgb(71, 85, 105)"; // Slate
  if (value <= 6) return "rgb(74, 103, 85)"; // Transitional green-grey
  if (value <= 7) return "rgb(74, 103, 65)"; // Reclaim green
  if (value <= 8) return "rgb(34, 197, 94)"; // Green
  if (value <= 9) return "rgb(16, 185, 129)"; // Emerald
  return "rgb(5, 150, 105)"; // Vibrant connected green
};

// Get track gradient based on slider value - SPECTACULAR FULL GRADIENT VERSION
const getTrackGradient = (value, percentage) => {
  // Beautiful full spectrum gradient always visible (red → yellow → green)
  // The filled portion shows the gradient, unfilled is dimmed
  const fullGradient = `
    linear-gradient(to right, 
      rgb(239, 68, 68) 0%,
      rgb(245, 158, 11) 30%,
      rgb(234, 179, 8) 50%,
      rgb(132, 204, 22) 70%,
      rgb(34, 197, 94) 100%
    )
  `;
  
  // Overlay to dim the unfilled portion
  return `
    linear-gradient(to right,
      transparent 0%,
      transparent ${percentage}%,
      rgba(0, 0, 0, 0.7) ${percentage}%,
      rgba(0, 0, 0, 0.7) 100%
    ),
    ${fullGradient}
  `;
};

// Glow configuration based on user specs
const getGlowConfig = (value) => {
  if (value <= 3) {
    // States 1-3: No glow
    return { thumbGlow: 0, trackGlow: 0, hasPulse: false };
  }
  if (value <= 6) {
    // States 4-6: Soft color transition, minimal glow
    return { thumbGlow: 0, trackGlow: 0.1, hasPulse: false };
  }
  if (value <= 8) {
    // States 7-8: Gentle thumb halo
    return { thumbGlow: value === 7 ? 12 : 18, trackGlow: 0.2, hasPulse: false };
  }
  // States 9-10: Brighter glow with soft pulse on release
  return { thumbGlow: value === 9 ? 24 : 30, trackGlow: 0.35, hasPulse: true };
};

const AlignmentCheck = ({ isOpen, onClose, onComplete }) => {
  const navigate = useNavigate();
  const [step, setStep] = useState("intro"); // intro, slider, desired, complete
  const [sliderValue, setSliderValue] = useState(5);
  const [selectedState, setSelectedState] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [sessionConfig, setSessionConfig] = useState(null);
  const [showPulse, setShowPulse] = useState(false);
  const sliderRef = useRef(null);
  
  const getToken = () => localStorage.getItem("reclaim_token");
  
  const handleSliderChange = (e) => {
    const newValue = parseInt(e.target.value);
    setSliderValue(newValue);
    setShowPulse(false); // Reset pulse when sliding
  };
  
  const handleSliderRelease = () => {
    // Trigger pulse animation for states 9-10 on release
    if (sliderValue >= 9) {
      setShowPulse(true);
      // Auto-reset pulse after animation
      setTimeout(() => setShowPulse(false), 1000);
    }
  };
  
  const handleConfirmSlider = () => {
    setStep("desired");
  };
  
  const handleSelectState = async (state) => {
    setSelectedState(state);
    setIsSubmitting(true);
    
    try {
      const res = await axios.post(
        `${API_URL}/api/alignment/check`,
        {
          slider_value: sliderValue,
          desired_state: state.id
        },
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );
      
      setSessionConfig(res.data.session_config);
      setStep("complete");
      
      // Store completion for today
      localStorage.setItem("alignment_check_date", new Date().toDateString());
      localStorage.setItem("reclaim_last_alignment", new Date().toDateString());
      
    } catch (err) {
      toast.error("Failed to save alignment check");
    } finally {
      setIsSubmitting(false);
    }
  };
  
  const handleStartSession = () => {
    // Track alignment completion for smart suggestions
    trackActivity('alignment_completed', { 
      sliderValue, 
      desiredState: selectedState?.id 
    });
    
    // Navigate to Private Suite with the selected state and config
    // Navigate FIRST, then call callbacks to prevent flash-back
    navigate(`/private-suite?intent=${selectedState?.id}&length=${sessionConfig?.default_length || 5}&from=alignment`);
    
    // Call completion callback after navigation
    onComplete?.({
      sliderValue,
      stateWord: STATE_MAPPING[sliderValue],
      desiredState: selectedState,
      sessionConfig
    });
    onClose?.();
  };
  
  const glowConfig = getGlowConfig(sliderValue);
  const sliderColor = getSliderColor(sliderValue);
  const stateWord = STATE_MAPPING[sliderValue];
  const sliderPercentage = ((sliderValue - 1) / 9) * 100;
  
  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setStep("intro");
      setSliderValue(5);
      setSelectedState(null);
      setSessionConfig(null);
      setShowPulse(false);
    }
  }, [isOpen]);
  
  if (!isOpen) return null;
  
  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[100] flex items-center justify-center"
      >
        {/* Backdrop with dynamic ambient glow */}
        <motion.div
          className="absolute inset-0 bg-gradient-to-b from-[#1a1a2e] via-[#16213e] to-[#0f0f23]"
          animate={{
            background: sliderValue >= 7 
              ? `radial-gradient(circle at 50% 40%, rgba(74, 103, 65, ${glowConfig.trackGlow}) 0%, transparent 50%), linear-gradient(to bottom, #1a1a2e, #16213e, #0f0f23)`
              : "linear-gradient(to bottom, #1a1a2e, #16213e, #0f0f23)"
          }}
          transition={{ duration: 0.5 }}
        />
        
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-6 right-6 p-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors z-10"
          data-testid="alignment-close-btn"
        >
          <X size={20} className="text-white/70" />
        </button>
        
        {/* Content */}
        <div className="relative z-10 w-full max-w-md px-6 max-h-[90vh] overflow-y-auto">
          <AnimatePresence mode="wait">
            {/* STEP 1: Intro Quote */}
            {step === "intro" && (
              <motion.div
                key="intro"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                className="text-center py-12"
              >
                <motion.div
                  initial={{ scale: 0.8, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ delay: 0.2 }}
                  className="w-16 h-16 mx-auto mb-8 rounded-full bg-gradient-to-br from-[#4A6741]/30 to-[#4A6741]/10 flex items-center justify-center"
                >
                  <Sparkles className="w-8 h-8 text-[#4A6741]" />
                </motion.div>
                
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.4 }}
                  className="mb-12 space-y-3"
                >
                  <p className="text-white/90 text-xl leading-relaxed font-light italic">
                    "Awareness is the beginning of alignment.
                  </p>
                  <p className="text-white/90 text-xl leading-relaxed font-light italic">
                    Take a moment to notice where you are,
                  </p>
                  <p className="text-white/90 text-xl leading-relaxed font-light italic">
                    and allow yourself to gently shift."
                  </p>
                </motion.div>
                
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.8 }}
                >
                  <Button
                    onClick={() => setStep("slider")}
                    className="px-8 py-4 bg-gradient-to-r from-[#4A6741] to-[#5A7D51] hover:from-[#3D5A35] hover:to-[#4A6741] text-white rounded-full text-lg shadow-lg shadow-[#4A6741]/25"
                    data-testid="start-alignment-btn"
                  >
                    Start Alignment Check
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                </motion.div>
              </motion.div>
            )}
            
            {/* STEP 2: Slider */}
            {step === "slider" && (
              <motion.div
                key="slider"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                className="text-center py-8"
              >
                <h2 className="text-white/60 text-sm uppercase tracking-wider mb-2">
                  Alignment Check
                </h2>
                <p className="text-white/90 text-xl mb-12">
                  Where are you right now?
                </p>
                
                {/* State Word Display */}
                <motion.div
                  key={stateWord}
                  initial={{ scale: 0.9, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  className="mb-10"
                >
                  <p className="text-white/50 text-sm mb-2">Current State</p>
                  <motion.h1
                    className="text-4xl font-bold transition-all duration-300"
                    style={{ 
                      color: sliderColor,
                      textShadow: sliderValue >= 7 
                        ? `0 0 ${glowConfig.thumbGlow}px ${sliderColor}` 
                        : 'none'
                    }}
                    animate={sliderValue >= 9 ? {
                      scale: [1, 1.02, 1]
                    } : {}}
                    transition={{
                      duration: 2,
                      repeat: sliderValue >= 9 ? Infinity : 0,
                      ease: "easeInOut"
                    }}
                  >
                    {stateWord}
                  </motion.h1>
                </motion.div>
                
                {/* Slider with effects */}
                <div className="relative mb-12 px-4">
                  {/* Background glow for higher states */}
                  {sliderValue >= 7 && (
                    <motion.div
                      className="absolute inset-x-0 top-1/2 -translate-y-1/2 h-12 rounded-full blur-xl pointer-events-none"
                      style={{ backgroundColor: sliderColor }}
                      initial={{ opacity: 0 }}
                      animate={{ 
                        opacity: glowConfig.trackGlow,
                      }}
                      transition={{ duration: 0.3 }}
                    />
                  )}
                  
                  {/* Pulse animation on release for states 9-10 */}
                  {showPulse && (
                    <motion.div
                      className="absolute inset-x-0 top-1/2 -translate-y-1/2 h-16 rounded-full pointer-events-none"
                      style={{ backgroundColor: sliderColor }}
                      initial={{ opacity: 0.4, scale: 1 }}
                      animate={{ opacity: 0, scale: 1.5 }}
                      transition={{ duration: 0.8, ease: "easeOut" }}
                    />
                  )}
                  
                  <input
                    ref={sliderRef}
                    type="range"
                    min="1"
                    max="10"
                    value={sliderValue}
                    onChange={handleSliderChange}
                    onMouseUp={handleSliderRelease}
                    onTouchEnd={handleSliderRelease}
                    className="alignment-slider w-full rounded-full appearance-none cursor-pointer relative z-10"
                    style={{
                      background: getTrackGradient(sliderValue, sliderPercentage),
                    }}
                    data-testid="alignment-slider"
                  />
                  
                  {/* Slider labels - more detailed scale */}
                  <div className="flex justify-between mt-3 px-1">
                    <div className="text-center">
                      <span className="text-white/60 text-sm font-medium">Low</span>
                    </div>
                    <div className="text-center">
                      <span className="text-white/40 text-xs">|</span>
                    </div>
                    <div className="text-center">
                      <span className="text-white/40 text-xs">|</span>
                    </div>
                    <div className="text-center">
                      <span className="text-white/40 text-xs">|</span>
                    </div>
                    <div className="text-center">
                      <span className="text-white/60 text-sm font-medium">High</span>
                    </div>
                  </div>
                </div>
                
                <Button
                  onClick={handleConfirmSlider}
                  className="px-8 py-4 bg-gradient-to-r from-[#4A6741] to-[#5A7D51] hover:from-[#3D5A35] hover:to-[#4A6741] text-white rounded-full shadow-lg shadow-[#4A6741]/25"
                  data-testid="confirm-slider-btn"
                >
                  Continue
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </motion.div>
            )}
            
            {/* STEP 3: Desired State - 2×4 Grid */}
            {step === "desired" && (
              <motion.div
                key="desired"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                className="text-center py-6"
              >
                <h2 className="text-white/90 text-xl mb-2">
                  Where would you like to move toward?
                </h2>
                <p className="text-white/50 text-sm mb-8">
                  Choose the state you would like to cultivate.
                </p>
                
                {/* 2×4 State cards grid */}
                <div className="grid grid-cols-2 gap-3 mb-6">
                  {DESIRED_STATES.map((state, index) => {
                    const Icon = state.icon;
                    return (
                      <motion.button
                        key={state.id}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.05 }}
                        onClick={() => handleSelectState(state)}
                        disabled={isSubmitting}
                        className={`
                          p-4 rounded-xl text-left transition-all duration-200
                          bg-white/5 hover:bg-white/10 border border-white/10 hover:border-white/20
                          ${isSubmitting ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
                          group
                        `}
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        data-testid={`state-${state.id}`}
                      >
                        <div className="flex items-start gap-3">
                          <div 
                            className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 transition-all duration-200 group-hover:scale-110"
                            style={{ backgroundColor: `${state.color}25` }}
                          >
                            <Icon size={18} style={{ color: state.color }} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <h3 className="text-white font-medium mb-0.5">{state.title}</h3>
                            <p className="text-white/50 text-xs leading-relaxed line-clamp-2">
                              {state.description}
                            </p>
                          </div>
                        </div>
                      </motion.button>
                    );
                  })}
                </div>
                
                {isSubmitting && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="flex items-center justify-center gap-2 text-white/60"
                  >
                    <div className="w-4 h-4 border-2 border-white/40 border-t-transparent rounded-full animate-spin" />
                    <span className="text-sm">Preparing your session...</span>
                  </motion.div>
                )}
              </motion.div>
            )}
            
            {/* STEP 4: Complete - Session Preview */}
            {step === "complete" && sessionConfig && (
              <motion.div
                key="complete"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                className="text-center py-8"
              >
                <motion.div
                  initial={{ scale: 0.8 }}
                  animate={{ scale: 1 }}
                  transition={{ type: "spring", stiffness: 200 }}
                  className="w-16 h-16 mx-auto mb-6 rounded-full bg-gradient-to-br from-[#4A6741] to-[#5A7D51] flex items-center justify-center shadow-lg shadow-[#4A6741]/30"
                >
                  <Check className="w-8 h-8 text-white" />
                </motion.div>
                
                <h2 className="text-white text-xl font-medium mb-2">
                  Alignment Check Complete
                </h2>
                <p className="text-white/60 mb-3">
                  You're at <span style={{ color: sliderColor }} className="font-medium">{stateWord}</span>, 
                  moving toward <span className="text-white font-medium">{selectedState?.title}</span>
                </p>
                
                {/* Transition message */}
                <motion.p
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.3 }}
                  className="text-white/50 text-sm italic mb-8"
                >
                  We've prepared a personalised Private Suite session to support this shift.
                </motion.p>
                
                {/* Session Preview Card */}
                <div className="bg-white/5 rounded-2xl p-6 mb-6 border border-white/10 text-left">
                  <h3 className="text-white/90 font-medium mb-4 text-center">Your Session</h3>
                  
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <span className="text-white/60 text-sm">Session Length</span>
                      <div className="flex items-center gap-2">
                        <span className="text-white font-medium">
                          {sessionConfig.default_length} minutes
                        </span>
                        {!sessionConfig.is_premium && (
                          <span className="text-xs text-amber-400 flex items-center gap-1 bg-amber-400/10 px-2 py-0.5 rounded-full">
                            <Lock size={10} />
                            Preview
                          </span>
                        )}
                      </div>
                    </div>
                    
                    <div className="flex items-center justify-between">
                      <span className="text-white/60 text-sm">Intent</span>
                      <div className="flex items-center gap-2">
                        {selectedState?.icon && (
                          <selectedState.icon size={14} style={{ color: selectedState.color }} />
                        )}
                        <span className="text-white">{selectedState?.title}</span>
                      </div>
                    </div>
                    
                    {sessionConfig.is_premium && (
                      <div className="flex items-center justify-between">
                        <span className="text-white/60 text-sm">Available Lengths</span>
                        <div className="flex gap-1">
                          {sessionConfig.available_lengths.map(len => (
                            <span key={len} className="text-xs text-white/70 bg-white/10 px-2 py-0.5 rounded">
                              {len}m
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                  
                  {!sessionConfig.is_premium && (
                    <p className="text-white/40 text-xs mt-4 text-center border-t border-white/10 pt-4">
                      This is a 5-minute preview session. 
                      <br />
                      <span className="text-amber-400/80">Upgrade to unlock longer sessions and more features.</span>
                    </p>
                  )}
                </div>
                
                <Button
                  onClick={handleStartSession}
                  className="w-full py-4 bg-gradient-to-r from-[#4A6741] to-[#5A7D51] hover:from-[#3D5A35] hover:to-[#4A6741] text-white rounded-xl text-lg shadow-lg shadow-[#4A6741]/25"
                  data-testid="start-session-btn"
                >
                  Enter Your Private Suite
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
                
                <button
                  onClick={onClose}
                  className="mt-4 text-white/50 hover:text-white/70 text-sm transition-colors"
                  data-testid="skip-session-btn"
                >
                  Skip for now
                </button>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
        
        {/* Custom slider styles with dynamic glow - SPECTACULAR VERSION */}
        <style>{`
          .alignment-slider {
            -webkit-appearance: none;
            appearance: none;
            height: 12px;
            border-radius: 9999px;
          }
          
          .alignment-slider::-webkit-slider-thumb {
            -webkit-appearance: none;
            appearance: none;
            width: 32px;
            height: 32px;
            border-radius: 50%;
            background: linear-gradient(135deg, white 0%, #f0f0f0 100%);
            cursor: pointer;
            border: 3px solid white;
            box-shadow: 
              0 2px 8px rgba(0, 0, 0, 0.4),
              0 0 ${glowConfig.thumbGlow}px ${sliderColor},
              inset 0 1px 2px rgba(255, 255, 255, 0.5);
            transition: box-shadow 0.3s ease, transform 0.15s ease;
          }
          
          .alignment-slider::-webkit-slider-thumb:hover {
            transform: scale(1.15);
            box-shadow: 
              0 4px 12px rgba(0, 0, 0, 0.5),
              0 0 ${Math.max(glowConfig.thumbGlow, 15)}px ${sliderColor},
              inset 0 1px 2px rgba(255, 255, 255, 0.5);
          }
          
          .alignment-slider::-webkit-slider-thumb:active {
            transform: scale(1.05);
          }
          
          .alignment-slider::-moz-range-thumb {
            width: 32px;
            height: 32px;
            border-radius: 50%;
            background: linear-gradient(135deg, white 0%, #f0f0f0 100%);
            cursor: pointer;
            border: 3px solid white;
            box-shadow: 
              0 2px 8px rgba(0, 0, 0, 0.4),
              0 0 ${glowConfig.thumbGlow}px ${sliderColor},
              inset 0 1px 2px rgba(255, 255, 255, 0.5);
            transition: box-shadow 0.3s ease;
          }
          
          .alignment-slider:focus {
            outline: none;
          }
          
          .alignment-slider::-webkit-slider-runnable-track {
            height: 12px;
            border-radius: 9999px;
          }
          
          .alignment-slider::-moz-range-track {
            height: 12px;
            border-radius: 9999px;
          }
        `}</style>
      </motion.div>
    </AnimatePresence>
  );
};

export default AlignmentCheck;
